import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { formatPrice, getAllPriceFormats, getAllMonthlyPriceFormats } from '../utils/currency';
import ImageSeparator from '../components/ImageSeparator';
import {
  CheckCircle,
  XCircle,
  Download,
  Calculator,
  Heart,
  Stethoscope,
  Shield,
  TrendingUp,
  Video,
  Phone,
  MessageCircle,
  DollarSign,
  BarChart3,
  PieChart,
  AlertTriangle,
  Info
} from 'lucide-react';

const CoveragePricing = () => {
  const coverageItems = [
    {
      category: "Urgent Care & Clinical Treatment",
      coverage: "100%",
      description: "100% coverage at AMHAS Urgent Care Facilities",
      items: [
        "Non-emergency medical visits and evaluations",
        "Urgent care services",
        "Clinical treatment services",
        "World-class doctor consultations",
        "Immediate care access"
      ],
      color: "primary"
    },
    {
      category: "Laboratory Services",
      coverage: "100%",
      description: "100% coverage at AMHAS Urgent Care Facilities",
      items: [
        "Complete lab work and blood testing",
        "Diagnostic laboratory services",
        "Blood work analysis",
        "On-site laboratory access"
      ],
      color: "accent"
    },
    {
      category: "Radiology & Diagnostic Imaging",
      coverage: "100%",
      description: "100% coverage at AMHAS Urgent Care Facilities",
      items: [
        "X-rays and diagnostic scans",
        "Advanced imaging services",
        "Radiology consultations",
        "Diagnostic imaging"
      ],
      color: "secondary"
    },
    {
      category: "Pharmaceuticals & Medications",
      coverage: "100%",
      description: "100% coverage at AMHAS Urgent Care Facilities",
      items: [
        "Essential pharmaceuticals",
        "Prescription medications",
        "On-site pharmacy access",
        "Medication dispensing"
      ],
      color: "primary"
    },
    {
      category: "Telehealth & AI",
      coverage: "100%",
      description: "AI triage at school with Western doctor review, treatment guidance, and referrals",
      items: [
        "AI pre‑diagnosis from school‑collected vitals & symptoms",
        "Video/audio consultations with Western doctors",
        "Asynchronous consults (photos, messages)",
        "Second opinions & care plan adjustments",
        "Referral to AMHAS Urgent Care when needed",
        "Medication guidance and care plans"
      ],
      color: "accent"
    },
    {
      category: "Observation Beds & Extended Care",
      coverage: "100%",
      description: "100% coverage at AMHAS Urgent Care Facilities",
      items: [
        "Observation beds for extended treatment",
        "Non-surgical emergency care",
        "Extended monitoring capabilities",
        "24/7 access to care"
      ],
      color: "secondary"
    }
  ];

  const exclusions = [
    "Chronic and terminal illnesses",
    "Major or orthopedic surgeries",
    "Hospitalisation longer than 3 months",
    "Pandemic-related illnesses",
    "Cosmetic procedures",
    "Experimental treatments",
    "Pre-existing conditions diagnosed before enrollment"
  ];

  const costExamples = [
    {
      service: "Malaria Treatment",
      withoutAMHAS: 15000,
      withAMHAS: 3000,
      savings: 12000,
      description: "Complete malaria treatment including consultation, medication, and follow-up"
    },
    {
      service: "Pneumonia (1 week)",
      withoutAMHAS: 45000,
      withAMHAS: 13500,
      savings: 31500,
      description: "One week hospitalization for pneumonia treatment"
    },
    {
      service: "Routine Checkup",
      withoutAMHAS: 8000,
      withAMHAS: 0,
      savings: 8000,
      description: "Annual health checkup with basic tests"
    },
    {
      service: "Telehealth Consult",
      withoutAMHAS: 5000,
      withAMHAS: 0,
      savings: 5000,
      description: "Consultation with Western board-certified doctor"
    },
    {
      service: "Minor Surgery",
      withoutAMHAS: 25000,
      withAMHAS: 10000,
      savings: 15000,
      description: "Minor surgical procedure with 2-day hospital stay"
    }
  ];

  const pricingInfo = getAllPriceFormats();
  const monthlyPricingInfo = getAllMonthlyPriceFormats();
  
  const pricingTiers = [
    {
      name: "Standard Enrollment",
      price: pricingInfo.usd,
      priceCFA: pricingInfo.cfa,
      priceGMD: pricingInfo.gmd,
      monthlyPrice: monthlyPricingInfo.usd,
      monthlyPriceCFA: monthlyPricingInfo.cfa,
      monthlyPriceGMD: monthlyPricingInfo.gmd,
      description: "Single price for all families - no urban/rural distinction",
      features: [
        "100% coverage at AMHAS Urgent Care Facilities",
        "AI + Telehealth services at school",
        "All covered services included",
        "Biometric ID verification",
        "24/7 urgent care access",
        "Preventive care programs"
      ]
    }
  ];

  const addOns = [
    {
      name: "Medical Transportation Support",
      price: "Varies",
      description: "Support for transportation to AMHAS Urgent Care Facilities when needed"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_doctor_Care_1.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Coverage & Pricing</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              Transparent, affordable healthcare coverage for every child
            </p>
          </motion.div>
        </div>
      </section>

      {/* Coverage Overview */}
      <section className="relative section-padding bg-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_front_area_1.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">What's Covered</h2>
            <p className="section-subtitle">
              Comprehensive coverage designed to keep children healthy and families financially protected
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {coverageItems.map((item, index) => (
              <motion.div
                key={item.category}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-8"
              >
                <div className="text-center mb-6">
                  <div className="text-3xl font-bold text-primary-600 mb-2">{item.coverage}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.category}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>

                <ul className="space-y-3">
                  {item.items.map((service, serviceIndex) => (
                    <li key={serviceIndex} className="flex items-start space-x-3">
                      <CheckCircle size={16} className="text-accent-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{service}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Separator Banner */}
      <ImageSeparator 
        image="/urgentcare_pharm_1.png"
        title="Real Cost Savings"
        subtitle="See how much families save with AMHAS coverage compared to out-of-pocket expenses"
      />

      {/* Cost Examples */}
      <section className="relative section-padding bg-gray-50 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_lab_2.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Real Cost Examples</h2>
            <p className="section-subtitle">
              See how much families save with AMHAS coverage
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {costExamples.map((example, index) => (
              <motion.div
                key={example.service}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-6"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">{example.service}</h3>
                <p className="text-sm text-gray-600 mb-4 text-center">{example.description}</p>

                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-red-50 rounded">
                    <span className="text-gray-700 text-sm">Without AMHAS:</span>
                    <span className="font-bold text-red-600">{example.withoutAMHAS.toLocaleString()} CFA</span>
                  </div>

                  <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                    <span className="text-gray-700 text-sm">With AMHAS:</span>
                    <span className="font-bold text-green-600">{example.withAMHAS.toLocaleString()} CFA</span>
                  </div>

                  <div className="text-center p-2 bg-primary-50 rounded">
                    <span className="text-primary-800 font-bold text-sm">Savings: {example.savings.toLocaleString()} CFA</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Separator Banner */}
      <ImageSeparator 
        image="/urgentcare_scan_card_get_care.png"
        title="Affordable Pricing Options"
        subtitle="Simple, transparent pricing with one price for all families"
      />

      {/* Pricing Tiers */}
      <section className="relative section-padding bg-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="/urgentcare_front_waiting_Area.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Pricing Options</h2>
            <p className="section-subtitle">
              Flexible pricing to fit every family's needs
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-8 max-w-2xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`card p-8 ${index === 1 ? 'ring-2 ring-primary-600 relative' : ''}`}
              >
                {index === 1 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{tier.name}</h3>
                  <div className="text-4xl font-bold text-primary-600 mb-1">{tier.price}</div>
                  <div className="text-lg text-gray-600 mb-2">{tier.priceCFA}</div>
                  <div className="text-lg text-gray-600 mb-2">{tier.priceGMD}</div>
                  <div className="text-sm text-gray-500 mt-3 mb-1">Monthly equivalent:</div>
                  <div className="text-xl font-semibold text-gray-700 mb-1">{tier.monthlyPrice}</div>
                  <div className="text-sm text-gray-600 mb-1">{tier.monthlyPriceCFA}</div>
                  <div className="text-sm text-gray-600 mb-4">{tier.monthlyPriceGMD}</div>
                  <p className="text-gray-600">{tier.description}</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {tier.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start space-x-3">
                      <CheckCircle size={16} className="text-accent-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/student-enrollment" className="btn-primary w-full text-center">
                  Enroll Now
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Optional Add-ons</h2>
            <p className="section-subtitle">
              Enhance your coverage with additional services
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-8 max-w-2xl mx-auto">
            {addOns.map((addon, index) => (
              <motion.div
                key={addon.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-6"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-2">{addon.name}</h3>
                <div className="text-2xl font-bold text-primary-600 mb-3">{addon.price}</div>
                <p className="text-gray-600 text-sm">{addon.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Exclusions */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="section-title text-center">What's Not Covered</h2>
            <p className="section-subtitle text-center">
              Clear exclusions to set proper expectations
            </p>

            <div className="card p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {exclusions.map((exclusion, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <XCircle size={16} className="text-red-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{exclusion}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Information */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">Simple, Transparent Pricing</h2>
            <p className="text-xl text-primary-100 mb-8">
              One price for all families - no urban/rural distinction. Coverage for children ages 2-18.
            </p>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8">
              <div className="text-5xl font-bold mb-4">{pricingInfo.usd}</div>
              <div className="text-2xl mb-2">{pricingInfo.cfa}</div>
              <div className="text-2xl mb-4">{pricingInfo.gmd}</div>
              <div className="text-sm text-primary-200 mt-4 mb-2">Monthly equivalent:</div>
              <div className="text-2xl font-semibold mb-2">{monthlyPricingInfo.usd}</div>
              <div className="text-lg mb-1">{monthlyPricingInfo.cfa}</div>
              <div className="text-lg mb-4">{monthlyPricingInfo.gmd}</div>
              <p className="text-primary-100">
                Annual premium covers all services at AMHAS Urgent Care Facilities and AI + Telehealth at school
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTAs */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Ready to Enroll?</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Join thousands of families who have already protected their children's health with AMHAS.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/student-enrollment" className="btn-primary">
                Enroll Now
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CoveragePricing;
